/**
 * Client Pending Actions Hook
 * 
 * Fetches all pending actions requiring client attention across the platform.
 * Returns categorized counts and items for display in dashboard stat cards and modals.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export type PendingActionType = 
  | 'bids_to_review'
  | 'rfq_questions'
  | 'milestone_approval'
  | 'milestone_changes'
  | 'client_milestones'
  | 'job_completions'
  | 'delivery_proofs'
  | 'po_adjustments'
  | 'po_cancellations'
  | 'disputes'
  | 'po_to_issue';

export interface PendingAction {
  id: string;
  type: PendingActionType;
  jobId: string;
  jobPublicId: string;
  jobTitle: string;
  count?: number;
  poPublicId?: string;
  navigationUrl: string;
  description: string;
  createdAt?: string;
}

export interface PendingActionCategory {
  type: PendingActionType;
  label: string;
  icon: string;
  actions: PendingAction[];
  count: number;
}

export interface ClientPendingActions {
  totalCount: number;
  categories: PendingActionCategory[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const ACTION_LABELS: Record<PendingActionType, { label: string; icon: string }> = {
  po_to_issue: { label: 'Purchase Orders to Issue', icon: '📝' },
  bids_to_review: { label: 'Bids to Review', icon: '📋' },
  rfq_questions: { label: 'RFQ Questions', icon: '❓' },
  milestone_approval: { label: 'Milestone Approvals', icon: '📅' },
  milestone_changes: { label: 'Change Requests', icon: '🔄' },
  client_milestones: { label: 'Your Milestones Due', icon: '⏰' },
  job_completions: { label: 'Jobs to Review', icon: '✅' },
  delivery_proofs: { label: 'Deliveries to Review', icon: '📦' },
  po_adjustments: { label: 'PO Change Orders', icon: '💰' },
  po_cancellations: { label: 'Cancellation Requests', icon: '🚫' },
  disputes: { label: 'Active Disputes', icon: '⚠️' },
};

export const useClientPendingActions = (): ClientPendingActions => {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['client-pending-actions', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');

      // Get user's organization
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.organization_id) {
        return { actions: [] as PendingAction[] };
      }

      const orgId = profile.organization_id;
      const actions: PendingAction[] = [];

      // Run all queries in parallel
      const [
        bidsResult,
        questionsResult,
        approvalsResult,
        changesResult,
        completionsResult,
        proofsResult,
        adjustmentsResult,
        disputesResult,
        milestonesResult,
        poToIssueResult,
      ] = await Promise.all([
        // 1. Bids to Review - count bids per job
        supabase
          .from('bids')
          .select(`
            id,
            rfq_id,
            rfqs!inner (
              id,
              job_id,
              jobs!inner (
                id,
                public_id,
                title,
                organization_id,
                is_deleted
              )
            )
          `)
        .eq('status', 'submitted')
        .eq('rfqs.jobs.organization_id', orgId)
        .eq('rfqs.jobs.is_deleted', false)
        .eq('rfqs.jobs.status', 'rfq_open'),

        // 2. RFQ Questions to Answer
        supabase
          .from('rfq_questions')
          .select(`
            id,
            question,
            created_at,
            rfqs!inner (
              id,
              job_id,
              jobs!inner (
                id,
                public_id,
                title,
                organization_id,
                is_deleted
              )
            )
          `)
        .is('answer', null)
        .eq('rfqs.jobs.organization_id', orgId)
        .eq('rfqs.jobs.is_deleted', false)
        .eq('rfqs.jobs.status', 'rfq_open'),

        // 3. Milestone Approvals Pending
        supabase
          .from('milestone_approvals')
          .select(`
            id,
            job_id,
            approval_status,
            jobs!inner (
              id,
              public_id,
              title,
              organization_id,
              is_deleted
            )
          `)
          .in('approval_status', ['pending_both', 'pending_client', 'approved_by_vendor'])
          .eq('jobs.organization_id', orgId)
          .eq('jobs.is_deleted', false),

        // 4. Milestone Change Requests from Vendor
        supabase
          .from('milestone_pending_changes')
          .select(`
            id,
            job_id,
            created_at,
            milestones!inner (
              milestone_name
            ),
            jobs!inner (
              id,
              public_id,
              title,
              organization_id,
              is_deleted
            )
          `)
          .eq('status', 'pending')
          .eq('requested_by_type', 'vendor')
          .eq('jobs.organization_id', orgId)
          .eq('jobs.is_deleted', false),

        // 5. Job Completions to Review
        supabase
          .from('jobs')
          .select('id, public_id, title')
          .eq('organization_id', orgId)
          .eq('status', 'completed_pending_review')
          .eq('is_deleted', false),

        // 6. Delivery Proofs to Review
        supabase
          .from('delivery_proofs')
          .select(`
            id,
            job_id,
            created_at,
            jobs!inner (
              id,
              public_id,
              title,
              organization_id,
              is_deleted
            )
          `)
          .eq('client_review_status', 'pending')
          .eq('jobs.organization_id', orgId)
          .eq('jobs.is_deleted', false),

        // 7. PO Adjustment & Cancellation Requests
        supabase
          .from('po_modification_requests')
          .select(`
            id,
            request_type,
            status,
            cancellation_status,
            initiating_party,
            created_at,
            purchase_orders!inner (
              id,
              public_id,
              job_id,
              jobs!inner (
                id,
                public_id,
                title,
                organization_id,
                is_deleted
              )
            )
          `)
          .eq('initiating_party', 'vendor')
          .eq('purchase_orders.jobs.organization_id', orgId)
          .eq('purchase_orders.jobs.is_deleted', false),

        // 8. Active Disputes
        supabase
          .from('disputes')
          .select(`
            id,
            job_id,
            status,
            created_at,
            jobs!inner (
              id,
              public_id,
              title,
              organization_id,
              is_deleted
            )
          `)
          .in('status', ['open', 'under_review'])
          .eq('jobs.organization_id', orgId)
          .eq('jobs.is_deleted', false),

        // 9. Client Milestones Due (first incomplete user milestone per job where action_owner = 'client')
        // Only show milestones after both client and vendor have approved the milestone timeline
        supabase
          .from('milestones')
          .select(`
            id,
            job_id,
            milestone_name,
            due_date,
            action_owner,
            milestone_order,
            jobs!inner (
              id,
              public_id,
              title,
              organization_id,
              is_deleted,
              status,
              milestone_approvals!inner (
                approval_status
              )
            )
          `)
          .eq('is_completed', false)
          .eq('is_system_milestone', false)
          .eq('is_blank', false)
          .eq('action_owner', 'client')
          .eq('jobs.organization_id', orgId)
          .eq('jobs.is_deleted', false)
          .in('jobs.status', ['awarded', 'in_progress', 'completed_pending_review'])
          .eq('jobs.milestone_approvals.approval_status', 'approved_by_both')
          .order('milestone_order', { ascending: true }),

        // 10. Jobs Ready for PO Issuance (awarded + milestones approved + no PO)
        supabase
          .from('jobs')
          .select(`
            id,
            public_id,
            title,
            milestone_approvals!inner (
              approval_status
            )
          `)
          .eq('organization_id', orgId)
          .eq('status', 'awarded')
          .eq('is_deleted', false)
          .eq('milestone_approvals.approval_status', 'approved_by_both'),
      ]);

      // Process Bids - group by job
      if (bidsResult.data) {
        const bidsByJob = new Map<string, { job: any; count: number }>();
        bidsResult.data.forEach((bid: any) => {
          const job = bid.rfqs?.jobs;
          if (job) {
            const existing = bidsByJob.get(job.id);
            if (existing) {
              existing.count++;
            } else {
              bidsByJob.set(job.id, { job, count: 1 });
            }
          }
        });
        bidsByJob.forEach(({ job, count }) => {
          actions.push({
            id: `bids-${job.id}`,
            type: 'bids_to_review',
            jobId: job.id,
            jobPublicId: job.public_id,
            jobTitle: job.title,
            count,
            navigationUrl: `/jobs/${job.public_id}?tab=rfq`,
            description: `${count} bid${count > 1 ? 's' : ''} waiting for review`,
          });
        });
      }

      // Process RFQ Questions
      if (questionsResult.data) {
        const questionsByJob = new Map<string, { job: any; count: number }>();
        questionsResult.data.forEach((q: any) => {
          const job = q.rfqs?.jobs;
          if (job) {
            const existing = questionsByJob.get(job.id);
            if (existing) {
              existing.count++;
            } else {
              questionsByJob.set(job.id, { job, count: 1 });
            }
          }
        });
        questionsByJob.forEach(({ job, count }) => {
          actions.push({
            id: `questions-${job.id}`,
            type: 'rfq_questions',
            jobId: job.id,
            jobPublicId: job.public_id,
            jobTitle: job.title,
            count,
            navigationUrl: `/jobs/${job.public_id}?tab=rfq`,
            description: `${count} unanswered question${count > 1 ? 's' : ''}`,
          });
        });
      }

      // Process Milestone Approvals
      if (approvalsResult.data) {
        approvalsResult.data.forEach((approval: any) => {
          const job = approval.jobs;
          if (job) {
            actions.push({
              id: `approval-${approval.id}`,
              type: 'milestone_approval',
              jobId: job.id,
              jobPublicId: job.public_id,
              jobTitle: job.title,
              navigationUrl: `/jobs/${job.public_id}/milestones`,
              description: 'Milestone timeline pending your approval',
            });
          }
        });
      }

      // Process Milestone Change Requests
      if (changesResult.data) {
        changesResult.data.forEach((change: any) => {
          const job = change.jobs;
          const milestone = change.milestones;
          if (job) {
            actions.push({
              id: `change-${change.id}`,
              type: 'milestone_changes',
              jobId: job.id,
              jobPublicId: job.public_id,
              jobTitle: job.title,
              navigationUrl: `/jobs/${job.public_id}/milestones`,
              description: `Change request for "${milestone?.milestone_name || 'milestone'}"`,
              createdAt: change.created_at,
            });
          }
        });
      }

      // Process Job Completions
      if (completionsResult.data) {
        completionsResult.data.forEach((job: any) => {
          actions.push({
            id: `completion-${job.id}`,
            type: 'job_completions',
            jobId: job.id,
            jobPublicId: job.public_id,
            jobTitle: job.title,
            navigationUrl: `/jobs/${job.public_id}`,
            description: 'Job completion pending review',
          });
        });
      }

      // Process Delivery Proofs
      if (proofsResult.data) {
        proofsResult.data.forEach((proof: any) => {
          const job = proof.jobs;
          if (job) {
            actions.push({
              id: `proof-${proof.id}`,
              type: 'delivery_proofs',
              jobId: job.id,
              jobPublicId: job.public_id,
              jobTitle: job.title,
              navigationUrl: `/jobs/${job.public_id}?tab=shipping`,
              description: 'Delivery proof pending review',
              createdAt: proof.created_at,
            });
          }
        });
      }

      // Process PO Adjustments and Cancellations
      if (adjustmentsResult.data) {
        adjustmentsResult.data.forEach((req: any) => {
          const po = req.purchase_orders;
          const job = po?.jobs;
          if (job && po) {
            if (req.request_type === 'cancellation' && req.cancellation_status === 'pending_client_approval') {
              actions.push({
                id: `cancellation-${req.id}`,
                type: 'po_cancellations',
                jobId: job.id,
                jobPublicId: job.public_id,
                jobTitle: job.title,
                poPublicId: po.public_id,
                navigationUrl: `/purchase-orders/${po.public_id}`,
                description: 'Cancellation fee pending your approval',
                createdAt: req.created_at,
              });
            } else if (req.request_type !== 'cancellation' && req.status === 'pending') {
              actions.push({
                id: `adjustment-${req.id}`,
                type: 'po_adjustments',
                jobId: job.id,
                jobPublicId: job.public_id,
                jobTitle: job.title,
                poPublicId: po.public_id,
                navigationUrl: `/purchase-orders/${po.public_id}?tab=adjustments`,
                description: 'PO change order pending approval',
                createdAt: req.created_at,
              });
            }
          }
        });
      }

      // Process Disputes
      if (disputesResult.data) {
        disputesResult.data.forEach((dispute: any) => {
          const job = dispute.jobs;
          if (job) {
            actions.push({
              id: `dispute-${dispute.id}`,
              type: 'disputes',
              jobId: job.id,
              jobPublicId: job.public_id,
              jobTitle: job.title,
              navigationUrl: `/jobs/${job.public_id}`,
              description: `Dispute ${dispute.status === 'open' ? 'open' : 'under review'}`,
              createdAt: dispute.created_at,
            });
          }
        });
      }

      // Process Client Milestones - get first incomplete per job
      if (milestonesResult.data) {
        const seenJobs = new Set<string>();
        milestonesResult.data.forEach((milestone: any) => {
          const job = milestone.jobs;
          if (job && !seenJobs.has(job.id)) {
            seenJobs.add(job.id);
            actions.push({
              id: `milestone-${milestone.id}`,
              type: 'client_milestones',
              jobId: job.id,
              jobPublicId: job.public_id,
              jobTitle: job.title,
              navigationUrl: `/jobs/${job.public_id}?tab=overview`,
              description: `"${milestone.milestone_name}" is your responsibility`,
            });
          }
        });
      }

      // Process PO to Issue - jobs awarded with approved milestones but no PO yet
      if (poToIssueResult.data && poToIssueResult.data.length > 0) {
        // Get job IDs that might need PO
        const jobIds = poToIssueResult.data.map((job: any) => job.id);
        
        // Check which jobs already have a purchase order
        const { data: existingPOs } = await supabase
          .from('purchase_orders')
          .select('job_id')
          .in('job_id', jobIds);
        
        const jobsWithPO = new Set(existingPOs?.map(po => po.job_id) || []);
        
        poToIssueResult.data.forEach((job: any) => {
          // Only add if job doesn't already have a PO
          if (!jobsWithPO.has(job.id)) {
            actions.push({
              id: `po-to-issue-${job.id}`,
              type: 'po_to_issue',
              jobId: job.id,
              jobPublicId: job.public_id,
              jobTitle: job.title,
              navigationUrl: `/jobs/${job.public_id}?tab=overview`,
              description: 'Ready for Purchase Order issuance',
            });
          }
        });
      }

      return { actions };
    },
    enabled: !!user,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Group actions by type
  const categories: PendingActionCategory[] = Object.entries(ACTION_LABELS).map(([type, { label, icon }]) => {
    const typeActions = data?.actions.filter(a => a.type === type) || [];
    return {
      type: type as PendingActionType,
      label,
      icon,
      actions: typeActions,
      count: typeActions.reduce((sum, a) => sum + (a.count || 1), 0),
    };
  }).filter(cat => cat.count > 0);

  const totalCount = categories.reduce((sum, cat) => sum + cat.count, 0);

  return {
    totalCount,
    categories,
    loading: isLoading,
    error: error?.message || null,
    refetch,
  };
};
